import React, { useState } from 'react';
import { NodeSelect, NodeTextField } from '../NodeUI';
import BlackButton from '../Component/Buttons/BlackButton';
import { send_request } from '../../utils/Request';
import './billing.css';

const ONBOARDING_PROVIDERS = [
  {
    key: 'OPENAI',
    name: 'OpenAI',
    placeholder: 'sk-...',
    steps: [
      'Sign in to the OpenAI platform (this is separate from ChatGPT).',
      'Under Settings → Billing, add a payment method and prepaid credits.',
      'On the API keys page, click "Create new secret key".',
      'Copy the key (starts with sk-) — it is shown only once — and paste it here.',
    ],
    note: 'Requires prepaid credits — OpenAI no longer offers free trial credits.',
    links: [
      { label: 'API keys', url: 'https://platform.openai.com/api-keys' },
      { label: 'Billing', url: 'https://platform.openai.com/settings/organization/billing/overview' },
    ],
  },
  {
    key: 'ANTHROPIC',
    name: 'Anthropic',
    placeholder: 'sk-ant-...',
    steps: [
      'Sign in to the Anthropic Console.',
      'Under Settings → Billing, add a card and buy credits ($5 minimum).',
      'Go to Settings → API Keys and click "Create Key".',
      'Copy the key (starts with sk-ant-) — it is shown only once — and paste it here.',
    ],
    note: 'The key returns errors until the account has credits.',
    links: [
      { label: 'API keys', url: 'https://console.anthropic.com/settings/keys' },
      { label: 'Billing', url: 'https://console.anthropic.com/settings/billing' },
    ],
  },
  {
    key: 'GOOGLE_AI',
    name: 'Google AI',
    placeholder: 'AIza...',
    steps: [
      'Sign in to Google AI Studio with your Google account.',
      'On the API keys page, click "Create API key".',
      'Choose "Create API key in new project" (one click).',
      'Copy the key (starts with AIza) and paste it here.',
    ],
    note: 'Free tier available — no card required to start.',
    links: [
      { label: 'Create API key', url: 'https://aistudio.google.com/app/apikey' },
      { label: 'Free tier limits', url: 'https://ai.google.dev/gemini-api/docs/rate-limits' },
    ],
  },
];

const PROVIDER_OPTIONS = ONBOARDING_PROVIDERS.map((provider) => ({
  value: provider.key,
  label: provider.name,
}));

export default function OnboardingProviderKeyForm({ onValidated, validating, setValidating }) {
  const [provider, setProvider] = useState('OPENAI');
  const [apiKey, setApiKey] = useState('');
  const [savedCredentialId, setSavedCredentialId] = useState(null);
  const [inlineError, setInlineError] = useState('');

  const selected = ONBOARDING_PROVIDERS.find((p) => p.key === provider);

  const saveCredential = async () => {
    const data = {
      type: 'AI_MODEL_KEY',
      credentialValue: apiKey.trim(),
      name: `AI Key - ${provider}`,
      data: { integrationType: provider },
    };
    if (savedCredentialId) {
      data.credentialId = savedCredentialId;
      const response = await send_request('ai-service/credential/user', data, '', 'PUT');
      if (response?.isAxiosError || response?.response) throw response;
      return savedCredentialId;
    }
    const response = await send_request('ai-service/credential/user', data, '', 'POST');
    if (response?.isAxiosError || response?.response) throw response;
    const id = response?.data?.credentialId || response?.data?.id;
    if (id) setSavedCredentialId(String(id));
    return id;
  };

  const handleValidate = async () => {
    if (!apiKey.trim() && !savedCredentialId) return;
    setValidating(true);
    setInlineError('');
    try {
      const credentialId = savedCredentialId || (await saveCredential());
      if (!credentialId) {
        setInlineError("Couldn't save your API key. Please try again.");
        return;
      }
      await onValidated({ credentialId: String(credentialId) });
    } catch (e) {
      const status = e?.response?.status;
      const message = e?.response?.data?.message;
      if (status === 503) {
        setInlineError("Your provider didn't respond. Try again in a moment.");
      } else {
        setInlineError(message || "Couldn't validate your API key. Please try again.");
      }
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="billing-provider-layout">
      <div className="billing-provider-grid">
        <NodeSelect
          label="Provider"
          value={provider}
          onChange={(value) => {
            setProvider(value);
            setInlineError('');
          }}
          options={PROVIDER_OPTIONS}
          inDialog
        />

        <NodeTextField
          label="API key"
          type="password"
          placeholder={selected?.placeholder}
          value={apiKey}
          onChange={(value) => {
            setApiKey(value);
            setInlineError('');
          }}
          error={Boolean(inlineError)}
          helperText={inlineError || undefined}
          inputProps={{ autoComplete: 'off' }}
        />

        <BlackButton
          variant="contained"
          fullWidth
          disabled={(!apiKey.trim() && !savedCredentialId) || validating}
          onClick={handleValidate}
        >
          {validating ? 'Validating key…' : 'Validate and continue'}
        </BlackButton>
      </div>

      {selected ? (
        <div className="billing-provider-instructions">
          <p className="billing-provider-instructions__title">
            How to get your {selected.name} key
          </p>
          <ol className="billing-provider-instructions__steps">
            {selected.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          {selected.note ? (
            <p className="billing-provider-instructions__note">{selected.note}</p>
          ) : null}
          <div className="billing-provider-instructions__links">
            {selected.links.map((link) => (
              <a
                key={link.url}
                className="billing-provider-instructions__link"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
